import {
  getStandardErrorObject,
  getTransactionId,
  logError,
  logInfo,
  getLoggingHeaders,
  getTokenBaseUrlByHostName,
  getCookieValue,
  fetchWithTimeout,
} from './Utils.js';
import { ClientErrors } from './ClientErrors.js';
import { OTP_CHALLENGE_ENDPOINT } from './Constants.js';

/**
 * Builds an error object out of the identity error response body. Mirrors the
 * `buildErrorObject` / `convertResponseToErroObject` helpers used on the sso-fe
 * side so the three failure branches below can inspect the same fields.
 */
function buildErrorObject(body) {
  try {
    const details = body.Error.details[0];
    return {
      code: details.errorCode,
      message: details.description || '',
      assessmentId: details.assessmentId,
      captchaImage: details.captchaImage,
      tokenId: details.lowScoreTokenId,
    };
  } catch {
    return { code: undefined, message: '' };
  }
}

function includes(message, fragment) {
  return (message || '').toLowerCase().trim().includes(fragment.toLowerCase());
}

/**
 * Sends the brand OTP sign in challenge code to the given email. This is the
 * client library equivalent of the `handleSendCode` step on the migrate sign in
 * page: it hits the identity `otp/challenge` endpoint and resolves with the
 * `otpReferenceToken` that the caller later uses to verify the code the user
 * received.
 *
 * The `recaptchaToken` and `siteKey` are generated by the caller (where the
 * grecaptcha widget lives) and passed in. `automationKey` is read from the
 * cookie. `recapthaImageValue` (and the `recapthaText` the user typed) only
 * come back from a previous low score error response and are forwarded on the
 * retry.
 *
 * @param {Object} params
 * @param {string} params.email - The email to send the OTP code to. Required.
 * @param {string} params.brand - The brand the user is signing in to. Required.
 * @param {string} [params.recaptchaToken] - Recaptcha token generated by the caller.
 * @param {string} [params.siteKey] - Recaptcha site key provided by the caller.
 * @param {string} [params.recapthaText] - Challenge text the user typed for the low score image path.
 * @param {Object} [params.recapthaImageValue] - The image challenge data returned by a previous error response.
 * @param {string} [params.recapthaImageValue.recapthaImage] - Base64 challenge image.
 * @param {string} [params.recapthaImageValue.assessmentId] - Recaptcha assessment id.
 * @param {string} [params.recapthaImageValue.tokenId] - Low score token id.
 * @returns {Promise<{ otpReferenceToken: string }>}
 */
export async function doBrandOtpSigninApiCall({
  email,
  brand,
  recaptchaToken = '',
  siteKey,
  recapthaText,
  recapthaImageValue,
} = {}) {
  // Generate a single transaction id and reuse it for every log/error so the
  // whole call can be traced as one unit in splunk.
  const transId = getTransactionId();
  const userEmail = (email || '').trim().toLowerCase();
  // True when the caller is re-submitting after a recaptcha image challenge
  // (recapthaImageValue + recapthaText come back from a previous error). Logged
  // on every line so we can tell the low score retries apart in splunk.
  const isLowScorePath = !!(recapthaImageValue?.recapthaImage && recapthaText);

  if (!userEmail) {
    logError(
      ClientErrors.BRAND_OTP_SIGNIN_EMAIL_REQUIRED,
      { email: userEmail, brand, isLowScorePath },
      transId,
    );
    throw getStandardErrorObject(
      ClientErrors.BRAND_OTP_SIGNIN_EMAIL_REQUIRED,
      transId,
    );
  }

  if (!brand) {
    logError(
      ClientErrors.BRAND_OTP_SIGNIN_BRAND_REQUIRED,
      { email: userEmail, brand, isLowScorePath },
      transId,
    );
    throw getStandardErrorObject(
      ClientErrors.BRAND_OTP_SIGNIN_BRAND_REQUIRED,
      transId,
    );
  }

  // automationKey is read from the cookie (set for automation runs). The
  // recaptchaToken and siteKey are generated by the caller and passed in.
  const automationKey = getCookieValue('automationKey');

  return new Promise((resolve, reject) => {
    // Build the request body exactly like `handleSendCode` on the migrate sign
    // in page: base fields plus one of the mutually exclusive recaptcha paths.
    const body = {
      userEmail,
      siteKey,
      recaptchaToken,
      brand,
    };

    if (automationKey) {
      body.automationKey = automationKey;
      // Don't send conflicting recaptcha inputs alongside the automation key.
      delete body.recaptchaToken;
      delete body.siteKey;
    } else if (recapthaImageValue?.recapthaImage && recapthaText) {
      // Low score path: the user is re-submitting after solving the image.
      body.isLowScorePath = 'true';
      body.captchaText = recapthaText;
      body.assessmentId = recapthaImageValue?.assessmentId;
      body.tokenId = recapthaImageValue?.tokenId;
      // The image challenge supersedes the original token/siteKey pair.
      delete body.recaptchaToken;
      delete body.siteKey;
    } else if (recaptchaToken) {
      body.recaptchaToken = recaptchaToken;
      body.siteKey = siteKey;
    } else {
      body.recaptchaToken = '';
    }

    // Log right before the api call (the equivalent of userService logging the
    // request before firing it).
    logInfo({
      logName: 'BRAND_OTP_SIGNIN_INITIATED',
      email: userEmail,
      brand,
      isLowScorePath,
      'Sfly-Transactionid': transId,
    });

    fetchWithTimeout(
      `${getTokenBaseUrlByHostName()}${OTP_CHALLENGE_ENDPOINT}`,
      {
        credentials: 'include',
        method: 'post',
        headers: {
          'Content-Type': 'application/json',
          ...getLoggingHeaders(),
        },
        body: JSON.stringify(body),
      },
      { retry: false },
    )
      .then((result) => {
        if (result.status === 200) {
          result
            .json()
            .then((data) => {
              logInfo({
                logName: 'BRAND_OTP_SIGNIN_SUCCESS',
                email: userEmail,
                brand,
                isLowScorePath,
                'Sfly-Transactionid': transId,
              });
              resolve({ otpReferenceToken: data.otpReferenceToken });
            })
            .catch((e) => {
              // A 200 with an unparseable body would otherwise leave the promise
              // pending forever; fail it so callers don't hang.
              logError(
                ClientErrors.BRAND_OTP_SIGNIN_FAILED,
                {
                  name: e?.name,
                  message: e?.message,
                  stack: e?.stack,
                  email: userEmail,
                  brand,
                  isLowScorePath,
                },
                transId,
              );
              reject(
                getStandardErrorObject(
                  ClientErrors.BRAND_OTP_SIGNIN_FAILED,
                  transId,
                ),
              );
            });
          return;
        }

        // Non success: parse the error body so we can mirror the three failure
        // branches handled by `handleSendCode` on the migrate sign in page.
        result
          .json()
          .catch(() => ({}))
          .then((errorBody) => {
            const exception = buildErrorObject(errorBody);

            // 1) The user typed the wrong recaptcha image challenge text.
            if (
              includes(exception.message, 'response: Invalid captchaText') ||
              includes(
                exception.message,
                'Error while verifying recaptcha token',
              )
            ) {
              logError(
                ClientErrors.BRAND_OTP_SIGNIN_INVALID_CAPTCHA_TEXT,
                { ...exception, email: userEmail, brand, isLowScorePath },
                transId,
              );
              reject(
                getStandardErrorObject(
                  ClientErrors.BRAND_OTP_SIGNIN_INVALID_CAPTCHA_TEXT,
                  transId,
                ),
              );
              return;
            }

            // 2) Low score path: the server wants the user to solve a recaptcha
            // image challenge. Surface the image/token so the caller can render
            // it (the recapthaImage branch in the existing code).
            if (
              includes(exception.message, 'Authorization failed') ||
              includes(exception.message, 'unauthorized recaptcha token')
            ) {
              logError(
                ClientErrors.BRAND_OTP_SIGNIN_RECAPTCHA_CHALLENGE,
                { ...exception, email: userEmail, brand, isLowScorePath },
                transId,
              );
              reject({
                ...getStandardErrorObject(
                  ClientErrors.BRAND_OTP_SIGNIN_RECAPTCHA_CHALLENGE,
                  transId,
                ),
                captchaImage: exception.captchaImage,
                assessmentId: exception.assessmentId,
                tokenId: exception.tokenId,
              });
              return;
            }

            // 3) Anything else is a generic failure.
            // Response's status/statusText/url are prototype getters, not own
            // properties, so spreading `result` loses them - pull them out
            // explicitly. `exception` is a plain object (from buildErrorObject),
            // so spreading that is safe.
            logError(
              ClientErrors.BRAND_OTP_SIGNIN_FAILED,
              {
                status: result.status,
                statusText: result.statusText,
                url: result.url,
                ...exception,
                email: userEmail,
                brand,
                isLowScorePath,
              },
              transId,
            );
            reject(
              getStandardErrorObject(
                ClientErrors.BRAND_OTP_SIGNIN_FAILED,
                transId,
              ),
            );
          });
      })
      .catch((e) => {
        logError(
          ClientErrors.BRAND_OTP_SIGNIN_FAILED,
          {
            name: e?.name,
            message: e?.message,
            stack: e?.stack,
            ...(e.response ?? {}),
            email: userEmail,
            brand,
            isLowScorePath,
          },
          transId,
        );
        reject(
          getStandardErrorObject(ClientErrors.BRAND_OTP_SIGNIN_FAILED, transId),
        );
      });
  });
}
